<template>
  <BaseModal
    :show="open"
    :title="$t('app.views.orders.modals.details.order_title', { id: order?.order_number || order?.id || 'N/A' })"
    @close="$emit('close')"
  >
    <!-- Status Badges Component -->
    <OrderStatusBadges :order="{ status: order.status || '', is_paid: order.is_paid || false }" />

    <!-- Basic Info Component -->
    <OrderBasicInfo :order="order" />

    <!-- Delivery Address Component -->
    <DeliveryAddressInfo :order="order" />

    <!-- Order Items Table Component -->
    <OrderItemsTable :order="order" />

    <!-- Order Totals Component -->
    <OrderTotals
      :order="order"
      :iva-enabled="ivaEnabled"
      :promotion-messages="promotionMessages"
    />
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth';
import { calculateIvaBreakdown } from '@/utils/ivaHelper';
// New componentized imports
import OrderStatusBadges from './OrderStatusBadges.vue'
import OrderBasicInfo from './OrderBasicInfo.vue'
import DeliveryAddressInfo from './DeliveryAddressInfo.vue'
import OrderItemsTable from './OrderItemsTable.vue'
import OrderTotals from './OrderTotals.vue'

const props = defineProps({
  open: {
    type: Boolean,
    required: true
  },
  order: {
    type: Object,
    required: false,
    default: () => ({
      id: '',
      status: '',
      customerName: '',
      table: '',
      type: 'Dine-in',
      createdAt: new Date(),
      items: [],
      subtotal: 0,
      tax: 0,
      taxRate: 0.1,
      total: 0,
      notes: ''
    })
  }
});

const emit = defineEmits(['close', 'status-update', 'paymentCompleted', 'openCashRegister', 'edit-order']);

const { t } = useI18n();
const authStore = useAuthStore();

// IVA state
const ivaEnabled = ref(props.order.iva_enabled || false);
const ivaRate = computed(() => authStore.restaurant?.iva_rate || 0.16);

const ivaCalculation = computed(() => {
  return calculateIvaBreakdown({
    subtotal: props.order.subtotal || 0,
    discount: props.order.promotion_discount || 0,
    deliveryFee: props.order.delivery_fee || 0,
    ivaRate: ivaRate.value,
    enabled: ivaEnabled.value
  });
});

const ivaAmount = computed(() => ivaCalculation.value.ivaAmount);
const totalWithIVA = computed(() => ivaCalculation.value.totalWithIva);




// Get promotion messages
const promotionMessages = computed(() => {
  if (!props.order.applied_promotions || !Array.isArray(props.order.applied_promotions) || props.order.applied_promotions.length === 0) {
    return [];
  }

  return props.order.applied_promotions
    .filter(promotion => promotion.message)
    .map(promotion => promotion.message);
});
</script>
